import React, { useCallback, useMemo } from "react";

import clsx from "clsx";

import ChangeFractionDisplay from "@components/change-fraction";
import {
  type AugmentedFactColumn,
  type AugmentedTableRow,
  VirtualizedTable,
} from "@components/reports/virtualized-table";
import { useUser } from "@components/user-context";
import {
  formatting,
  type Table,
  metrics,
  type IMCAttributionWindowDays,
} from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { downloadCsv } from "@utils/csv";

export const BreakdownView = ({
  breakdownTitle,
  breakdownSubtitle,
  rows,
  isLoading,
  breakdown,
  attributionWindow,
  snapshotSection,
}: {
  breakdownTitle: string;
  breakdownSubtitle?: string;
  rows: { current: MetricPoint; compare: MetricPoint }[];
  isLoading: boolean;
  breakdown: string;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  snapshotSection?: boolean;
}) => {
  const { user } = useUser();

  const metricDefs = useMemo(() => {
    const metrics: MetricBase[] = [
      "spend",
      "revenue",
      snapshotSection ? "mer" : "roas",
      "canc",
      "ecr",
      "ecpnv",
      "newVisits",
      "visits",
      "transactions",
    ];
    return metrics.map((v) => makeMetricDef(v, attributionWindow));
  }, [attributionWindow, snapshotSection]);

  const mapRow = useCallback(
    ({ labelValue, current, compare }) => {
      const link: Record<string, any> = {};
      const export_: Record<string, any> = {};
      const display: Record<string, string> = {};
      const changeFractions: Record<string, number> = {};
      const rowMetadata: Record<string, any> = {};

      display[breakdown] = labelValue || "(not set)";
      export_[breakdown] = labelValue || "(not set)";

      for (const metricDef of metricDefs) {
        const currentValue = metricDef.calculate(current);
        const compareValue = metricDef.calculate(compare);

        export_[metricDef.label] = currentValue;
        display[metricDef.label] = formatting.formatMetric.exact(
          currentValue,
          metricDef.format,
          {
            code: user.currencies.currentCurrency,
          },
        );
        changeFractions[metricDef.label] =
          (currentValue - compareValue) / compareValue;
      }

      return {
        id: labelValue,
        children: [],
        link,
        display,
        changeFractions,
        rowMetadata,
        cellHoverHTML: {},
        export: export_,
      } as AugmentedTableRow;
    },
    [breakdown, metricDefs, user.currencies.currentCurrency],
  );

  const totalRow = useMemo(() => {
    const labelValue = "Grand Total";
    const current: MetricPoint = {
      spend: 0,
      visits: 0,
      newVisits: 0,
      newCustomers: 0,
      transactions: 0,
      revenue: 0,
    };
    const compare: MetricPoint = {
      spend: 0,
      visits: 0,
      newVisits: 0,
      newCustomers: 0,
      transactions: 0,
      revenue: 0,
    };

    for (const { current: aCurrent, compare: aCompare } of rows) {
      for (const key in current) {
        current[key as keyof MetricPoint] += aCurrent[key as keyof MetricPoint];
        compare[key as keyof MetricPoint] += aCompare[key as keyof MetricPoint];
      }
    }

    return mapRow({ labelValue, current, compare });
  }, [rows, mapRow]);

  const tableRows: AugmentedTableRow[] = useMemo(() => {
    return rows.map(mapRow);
  }, [rows, mapRow]);

  const dimensionColumn: AugmentedFactColumn = useMemo(
    () => ({
      name: breakdown,
      key: breakdown,
    }),
    [breakdown],
  );

  const factColumns: AugmentedFactColumn[] = useMemo(() => {
    return metricDefs.map(
      (metricDef) =>
        ({
          name: metricDef.label,
          key: metricDef.label,
          headerTooltip: metricDef.descriptionHTML,
          comparison: {
            isPositiveChangeGood: metricDef.isPositiveChangeGood,
          },
          renderCell: (props) => {
            return (
              <div>
                <div>{props.row.display[metricDef.label]}</div>
                <div>
                  <ChangeFractionDisplay
                    changeFraction={props.row.changeFractions[metricDef.label]}
                    isPositiveChangeGood={metricDef.isPositiveChangeGood}
                  />
                </div>
              </div>
            );
          },
        } as AugmentedFactColumn),
    );
  }, [metricDefs]);

  const table: Table = useMemo(() => {
    return {
      title: "",
      dimensionColumns: [dimensionColumn],
      factColumns,
      rows: tableRows,
    };
  }, [dimensionColumn, factColumns, tableRows]);

  const cardClasses = clsx({
    card: true,
    rounded: true,
    "waiting-interstitial": isLoading,
    border: true,
  });

  return (
    <div className={cardClasses}>
      <div className="card-body">
        <div className="row">
          <div className="col">
            <h4 className="mb-3">
              <strong>{breakdownTitle}</strong>
            </h4>
            <p>{breakdownSubtitle}</p>
          </div>
          <div className="text-right mr-4">
            <PrimaryButton
              className="mr-auto"
              onClick={() =>
                downloadCsv("Sales", "north-beam-sales-report.csv", table)
              }
            >
              Export to CSV
            </PrimaryButton>
          </div>
        </div>
        <div className="row mt-3 nb-overview-page-table">
          <div className="col">
            <VirtualizedTable
              frozenRows={[totalRow]}
              rows={tableRows}
              dimensionColumn={dimensionColumn}
              factColumns={factColumns}
              rowHeight={60}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export type MetricPoint = {
  spend: number;
  visits: number;
  newVisits: number;
  newCustomers: number;
  transactions: number;
  revenue: number;
};

type MetricBase = keyof MetricPoint | "roas" | "ecpnv" | "canc" | "ecr" | "mer";

const MetricBaseLabels: { [key in MetricBase]: string } = {
  spend: "Spend",
  visits: "Visits",
  newVisits: "New Visits",
  newCustomers: "New Customers",
  transactions: "Txns",
  revenue: "Rev",
  roas: "ROAS",
  ecpnv: "eCPNV",
  canc: "TrueCAC",
  ecr: "ECR",
  mer: "MER",
};

const MetricBaseTypes: { [key in MetricBase]: metrics.MetricType } = {
  spend: metrics.MetricTypes.CURRENCY,
  visits: metrics.MetricTypes.INTEGER,
  newVisits: metrics.MetricTypes.INTEGER,
  newCustomers: metrics.MetricTypes.DECIMAL,
  transactions: metrics.MetricTypes.DECIMAL,
  revenue: metrics.MetricTypes.CURRENCY,
  roas: metrics.MetricTypes.MULTIPLIER,
  mer: metrics.MetricTypes.MULTIPLIER,
  ecpnv: metrics.MetricTypes.CURRENCY,
  canc: metrics.MetricTypes.CURRENCY,
  ecr: metrics.MetricTypes.PERCENTAGE,
};

function getMetricLabel(base: MetricBase, aw: IMCAttributionWindowDays) {
  const baseLabel = MetricBaseLabels[base];
  if (["spend", "visits", "newVisits"].includes(base)) {
    return baseLabel;
  }
  return `${baseLabel} (${aw === "infinity" ? "∞" : aw}d)`;
}

export type MetricDef = {
  label: string;
  format: metrics.MetricType;
  isPositiveChangeGood: boolean;
  calculate: (pt: MetricPoint) => number;
  descriptionHTML: string;
};

function makeMetricDef(
  base: MetricBase,
  aw: IMCAttributionWindowDays,
): MetricDef {
  const isPositiveChangeGood = base !== "canc" && base !== "ecpnv";
  const format = MetricBaseTypes[base];
  const label = getMetricLabel(base, aw);

  let calculate: (pt: MetricPoint) => number;
  if (base === "roas" || base === "mer") {
    calculate = (pt) => pt.revenue / pt.spend;
  } else if (base === "ecpnv") {
    calculate = (pt) => pt.spend / pt.newVisits;
  } else if (base === "canc") {
    calculate = (pt) => pt.spend / pt.newCustomers;
  } else if (base === "ecr") {
    calculate = (pt) => pt.transactions / pt.visits;
  } else {
    calculate = (pt) => pt[base];
  }

  let descriptionHTML = (
    {
      spend:
        "How much money was spent on this set of ads over the given time period.",
      visits: "Visits to your website.",
      newVisits:
        "Visits to your website from new visitors, as determined by the Northbeam Pixel.",
      newCustomers:
        "Attributed transactions from new customers on your online store.",
      transactions:
        "Attributed transactions from any customer on your online store.",
      revenue: "Attributed revenue from orders on your online store.",
      roas: "Return on Ad Spend - computed as Rev ÷ Spend.",
      mer: "Media Efficiency Ratio - computed as Rev ÷ Spend.",
      ecpnv: "Effective cost per new visit - calculated as Spend ÷ New Visits.",
      canc: "Cost to acquire a new customer - calculated as Spend ÷ New Customers.",
      ecr: "Ecommerce conversion rate - calculated as Txns ÷ Visits.",
    } as const
  )[base];

  descriptionHTML += `<hr /> ${makeAttributionWindowDescription(aw)}`;

  return {
    label,
    descriptionHTML,
    isPositiveChangeGood,
    format,
    calculate,
  };
}

function makeAttributionWindowDescription(aw: IMCAttributionWindowDays) {
  if (aw === "infinity") {
    return `This metric is reported with an <b>infinity-day attribution window</b>.
    Touchpoints receive credit for both delayed transactions and repeat
    transactions.`;
  }

  return `This metric is reported with a <b>${aw}-day attribution window</b>.
    Touchpoints only receive credit for transactions that happen within ${aw}
    ${aw === "1" ? "day" : "days"} of the touchpoint.`;
}
